import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface Template {
  name: string;
  path: string;
  source: 'vault' | 'central';
  size: number;
}

export function useTemplates(): UseQueryResult<{ templates: Template[]; default_folder: string }> {
  return useQuery({
    queryKey: ['obsidian', 'templates'],
    queryFn: () => api<{ templates: Template[]; default_folder: string }>('/api/obsidian/templates'),
  });
}

export function useTemplateContent(path: string | null): UseQueryResult<{ path: string; content: string }> {
  return useQuery({
    queryKey: ['obsidian', 'template-content', path],
    queryFn: () => api<{ path: string; content: string }>(
      `/api/obsidian/templates/content?path=${encodeURIComponent(path ?? '')}`,
    ),
    enabled: path !== null && path !== '',
  });
}

export function useFileHash(path: string | null): UseQueryResult<{ path: string; hash: string; size: number }> {
  return useQuery({
    queryKey: ['obsidian', 'file-hash', path],
    queryFn: () => api<{ path: string; hash: string; size: number }>(
      `/api/obsidian/file/hash?path=${encodeURIComponent(path ?? '')}`,
    ),
    enabled: path !== null && path !== '',
    staleTime: 0,
  });
}

export function useCreateFile(): ReturnType<
  typeof useMutation<{ path: string; hash: string; size: number }, Error, { path: string; content: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<{ path: string; hash: string; size: number }>('/api/obsidian/file', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['obsidian', 'tree'] });
    },
  });
}

export interface UpdateResponse {
  path: string;
  hash: string;
  size: number;
  conflict?: { conflict_file: string; current_hash: string };
}

export function useUpdateFile(): ReturnType<
  typeof useMutation<UpdateResponse, Error, { path: string; content: string; expected_hash: string | null }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<UpdateResponse>('/api/obsidian/file', { method: 'PUT', body }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['obsidian', 'file', vars.path] });
      qc.invalidateQueries({ queryKey: ['obsidian', 'tree'] });
    },
  });
}

export function useDeleteFile(): ReturnType<typeof useMutation<void, Error, string>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (path: string) =>
      api(`/api/obsidian/file?path=${encodeURIComponent(path)}`, { method: 'DELETE' }) as Promise<void>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['obsidian'] });
    },
  });
}

export function useMoveFile(): ReturnType<
  typeof useMutation<{ path: string }, Error, { from: string; to: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api<{ path: string }>('/api/obsidian/file/move', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['obsidian', 'tree'] }),
  });
}
